import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileSignature, CheckCircle2, ArrowRight, Clock, AlertCircle, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Phase5Notar = () => {
  const notarSchritte = [
    { titel: 'Notar auswählen & beauftragen', details: 'Käufer oder Verkäufer? Oft zahlt Käufer, wählt aber Verkäufer. Kosten: 1,5-2% des Kaufpreises.', pflicht: true },
    { titel: 'Kaufvertragsentwurf erstellen', details: 'Notar erstellt Entwurf basierend auf LOI und Verhandlungsergebnissen (10-20 Seiten)', pflicht: true },
    { titel: 'Entwurf an beide Parteien', details: 'Verkäufer & Käufer erhalten Entwurf mind. 2 Wochen vor Termin (gesetzliche Pflicht)', pflicht: true },
    { titel: 'Anwaltsprüfung', details: 'Beide Seiten lassen Kaufvertrag durch Fachanwalt prüfen (Kosten: € 2.000-5.000)', pflicht: true },
    { titel: 'Änderungswünsche einarbeiten', details: 'Notar überarbeitet Vertrag nach Feedback (meist 1-2 Iterationen)', pflicht: true },
    { titel: 'Vorkaufsrecht Mieter prüfen', details: 'Mieter hat 2 Monate Zeit, selbst zu kaufen (gesetzlich bei Gewerbe mit > 5 Jahren Laufzeit)', pflicht: true },
    { titel: 'Notartermin vereinbaren', details: 'Alle Parteien (Verkäufer, Käufer, ggf. Bevollmächtigte) müssen anwesend sein', pflicht: true },
    { titel: 'Kaufvertrag vorlesen & unterzeichnen', details: 'Notar liest gesamten Vertrag vor (1-2 Stunden), beide unterschreiben', pflicht: true },
    { titel: 'Auflassungsvormerkung eintragen', details: 'Notar trägt Vormerkung im Grundbuch ein (sichert Käufer ab)', pflicht: true },
    { titel: 'Kaufpreis-Fälligkeit', details: 'Meist: 50% nach Vormerkung, 50% nach Lastenfreistellung durch Bank', pflicht: true }
  ];

  const verantwortlichkeiten = [
    { wer: 'Eigentümer', aufgaben: ['Notar-Auswahl mitentscheiden', 'Kaufvertrag durch eigenen Anwalt prüfen lassen', 'Mieter über Vorkaufsrecht informieren (Frist: 2 Monate)', 'Zum Notartermin erscheinen (oder Vollmacht erteilen)', 'Bank über Verkauf informieren (Grundschuld-Ablösung klären)'] },
    { wer: 'RetailInvest', aufgaben: ['Notar-Empfehlungen geben (erfahren mit Gewerbeimmobilien)', 'Kaufvertragsentwurf auf kaufmännische Plausibilität prüfen', 'Zwischen Parteien vermitteln bei Uneinigkeit', 'Zeitplan koordinieren (Vorkaufsrecht, Notartermin)', 'Nach Beurkundung: Prozess bis Kaufpreiszahlung begleiten'] },
    { wer: 'Notar', aufgaben: ['Kaufvertrag rechtssicher erstellen', 'Grundbuch prüfen (Lasten, Rechte)', 'Vorkaufsrecht Mieter förmlich anzeigen', 'Auflassungsvormerkung eintragen', 'Kaufpreisfälligkeit überwachen', 'Eigentumsübergang im Grundbuch eintragen'] }
  ];

  const typischeProbleme = [
    { problem: 'Mieter übt Vorkaufsrecht aus und kauft selbst (zu gleichen Konditionen)', loesung: 'Passiert selten (< 5%), aber gesetzlich vorgeschrieben. Mieter muss gleichen Preis zahlen wie Drittkäufer. Falls Mieter kauft: Verkauf läuft normal weiter, nur anderer Käufer.' },
    { problem: 'Bank des Verkäufers verweigert Grundschuld-Ablösung (will höhere Vorfälligkeitsentschädigung)', loesung: 'VOR Notartermin mit Bank verhandeln. Vorfälligkeitsentschädigung ist gesetzlich gedeckelt (max. 1% des Restdarlehens bei > 10 Jahren). Notfalls: Käufer übernimmt Darlehen (bei günstigen Konditionen).' },
    { problem: 'Käufer zahlt Kaufpreis nicht fristgerecht (trotz Unterschrift)', loesung: 'Kaufvertrag enthält Rücktrittsklausel: Nach 4 Wochen Verzug kann Verkäufer zurücktreten + Schadensersatz fordern (meist 10% des Kaufpreises als Vertragsstrafe).' },
    { problem: 'Grundbuch-Eintragung dauert 3-6 Monate (statt geplant 4-8 Wochen)', loesung: 'Eigentumsübergang erfolgt trotzdem mit Kaufpreiszahlung ("wirtschaftlicher Eigentümer"). Käufer erhält Mieteinnahmen ab Zahlung, Verkäufer ist raus. Grundbucheintrag ist nur formaler Akt.' }
  ];

  const notarKosten = [
    { position: 'Kaufvertragserstellung & Beurkundung', prozent: '1,5%', beispiel: '€ 15.000 bei € 1 Mio.' },
    { position: 'Auflassungsvormerkung', prozent: '0,2%', beispiel: '€ 2.000 bei € 1 Mio.' },
    { position: 'Grundbucheintragung', prozent: '0,5%', beispiel: '€ 5.000 bei € 1 Mio.' },
    { position: 'Grunderwerbsteuer (zahlt Käufer)', prozent: '6,5%', beispiel: '€ 65.000 bei € 1 Mio. (NRW)' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Phase 5: Notartermin & Kaufvertrag – Verkaufsprozess Handelsimmobilie" description="Detaillierte Anleitung zu Phase 5: Notartermin, Kaufvertrag, Vorkaufsrecht Mieter, Auflassungsvormerkung, Kaufpreis-Fälligkeit, Kosten." url="/ablauf/phase-5-notar" />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/ablauf" className="text-sm hover:underline mb-4 inline-block" style={{ color: 'var(--navy-600)' }}>← Zurück zur Übersicht</Link>
          
          <div className="flex items-center gap-4 mb-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)' }}>
              <span className="text-4xl font-bold text-white">05</span>
            </div>
            <div>
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-2"></div>
              <h1 className="text-4xl font-bold" style={{ color: 'var(--navy-900)' }}>Notartermin & Kaufvertrag</h1>
              <div className="flex items-center gap-2 mt-2">
                <Clock className="h-5 w-5" style={{ color: 'var(--gold-600)' }} />
                <p className="text-lg font-semibold" style={{ color: 'var(--gray-700)' }}>Dauer: 2-4 Wochen (+ 2 Monate Vorkaufsrecht)</p>
              </div>
            </div>
          </div>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Warum ist diese Phase so wichtig?</h2>
            <p className="text-lg mb-4 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              <strong>Jetzt wird es rechtlich bindend.</strong> Mit Unterschrift beim Notar gibt es kein Zurück mehr (außer bei Vertragsverletzung). Ein schlecht formulierter Kaufvertrag kann zu jahrelangen Rechtsstreitigkeiten führen – besonders bei Gewährleistung, Haftung und Übergabemodalitäten.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              Das <strong>Vorkaufsrecht des Mieters</strong> ist gesetzlich vorgeschrieben und verzögert den Verkauf um 2 Monate. Viele Eigentümer wissen das nicht und sind überrascht. In dieser Phase ist Geduld gefragt – aber nach Notartermin ist der Verkauf faktisch durch (95% Erfolgsquote).
            </p>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Notartermin-Checkliste</h2>
            <div className="space-y-4">
              {notarSchritte.map((s, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: s.pflicht ? 'var(--gold-600)' : 'var(--navy-400)' }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold" style={{ color: 'var(--navy-800)' }}>{s.titel}</h3>
                      {s.pflicht && (
                        <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>
                          Pflicht
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-600)' }}>{s.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Notar- & Nebenkosten (Übersicht)</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: 'var(--gold-600)' }}>
                    <th className="text-left py-4 px-4 font-bold" style={{ color: 'var(--navy-800)' }}>Position</th>
                    <th className="text-left py-4 px-4 font-bold" style={{ color: 'var(--navy-800)' }}>Prozent</th>
                    <th className="text-left py-4 px-4 font-bold" style={{ color: 'var(--navy-800)' }}>Beispiel (€ 1 Mio. Kaufpreis)</th>
                  </tr>
                </thead>
                <tbody>
                  {notarKosten.map((k, i) => (
                    <tr key={i} className="border-b" style={{ borderColor: 'var(--gray-200)' }}>
                      <td className="py-4 px-4 font-semibold" style={{ color: 'var(--navy-700)' }}>{k.position}</td>
                      <td className="py-4 px-4 font-bold" style={{ color: 'var(--gold-600)' }}>{k.prozent}</td>
                      <td className="py-4 px-4 text-sm" style={{ color: 'var(--gray-700)' }}>{k.beispiel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm mt-4 italic" style={{ color: 'var(--gray-600)' }}>Hinweis: Grunderwerbsteuer zahlt der Käufer. Notarkosten werden oft 50/50 geteilt oder vom Käufer getragen (Verhandlungssache).</p>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Verantwortlichkeiten</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {verantwortlichkeiten.map((v, i) => (
                <div key={i} className="p-6 rounded-lg" style={{ backgroundColor: i === 1 ? 'var(--gold-100)' : 'var(--navy-50)' }}>
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>{v.wer}</h3>
                  <ul className="space-y-3">
                    {v.aufgaben.map((a, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                        <span className="font-bold" style={{ color: 'var(--gold-600)' }}>•</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Typische Probleme & Lösungen</h2>
            <div className="space-y-6">
              {typischeProbleme.map((item, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-red-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5" style={{ color: 'var(--error-600)' }} />
                      <h3 className="font-bold" style={{ color: 'var(--error-600)' }}>Problem:</h3>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-800)' }}>{item.problem}</p>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2" style={{ color: 'var(--success-600)' }}>Lösung:</h4>
                    <p className="text-sm" style={{ color: 'var(--gray-700)' }}>{item.loesung}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Expertentipp: Kritische Kaufvertrags-Klauseln</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-green-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--success-700)' }}>✓ Unbedingt verhandeln (als Verkäufer)</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>• <strong>Haftungsausschluss:</strong> "Verkauf wie besichtigt" (keine Gewährleistung)</li>
                  <li>• <strong>Kaufpreis-Fälligkeit:</strong> Max. 4 Wochen nach Notartermin</li>
                  <li>• <strong>Rücktrittsklausel:</strong> Bei Zahlungsverzug 10% Vertragsstrafe</li>
                  <li>• <strong>Übergabetermin:</strong> Erst nach vollständiger Kaufpreiszahlung</li>
                  <li>• <strong>Mieteinnahmen:</strong> Bis Übergabe gehen an Verkäufer</li>
                </ul>
              </div>
              <div className="p-6 bg-red-50 rounded-lg">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--error-700)' }}>✗ Vorsicht bei diesen Klauseln</h3>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <li>• "Verkäufer haftet 5 Jahre für versteckte Mängel" → ABLEHNEN</li>
                  <li>• "Kaufpreis-Ratenzahlung über 12 Monate" → Nur mit Bürgschaft</li>
                  <li>• "Übergabe vor Kaufpreiszahlung" → NIEMALS akzeptieren</li>
                  <li>• "Vorkaufsrecht Käufer bei Nachbar-Grundstück" → Unüblich</li>
                  <li>• "Verkäufer zahlt Maklercourtage Käufer" → Doppelbelastung vermeiden</li>
                </ul>
              </div>
            </div>
          </Card>

          <div className="flex gap-4">
            <Link to="/ablauf/phase-4-verhandlung" className="flex-1">
              <Button variant="outline" className="w-full font-semibold py-6" style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}>
                ← Zurück zu Phase 4
              </Button>
            </Link>
            <Link to="/ablauf/phase-6-uebergabe" className="flex-1">
              <Button className="w-full btn-gold-gradient text-white border-0 font-semibold py-6">
                Weiter zu Phase 6: Übergabe
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Phase5Notar;
